import { useState, useEffect } from 'react';
import {
    Button,
    Input,
    Select,
    Switch,
    InputNumber,
    Slider,
    Upload,
    Collapse,
    message
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    UploadOutlined,
    CloseOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Simple ID generator
const generateId = () => `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const { TextArea } = Input;
const { Panel } = Collapse;

export default function ActionBuilder({ step, onSave, onCancel }) {
    const [localStep, setLocalStep] = useState(null);
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        setLocalStep({ ...step });
    }, [step]);

    const handleChange = (field, value) => {
        setLocalStep(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        onSave(localStep);
    };

    if (!localStep) return null;

    return (
        <div className="action-builder">
            {contextHolder}

            {/* Message Step Editor */}
            {localStep.type === 'message' && (
                <MessageStepEditor
                    step={localStep}
                    onChange={handleChange}
                    messageApi={messageApi}
                />
            )}

            {/* Question Step Editor */}
            {localStep.type === 'question' && (
                <QuestionStepEditor
                    step={localStep}
                    onChange={handleChange}
                />
            )}

            {/* Delay Step Editor */}
            {localStep.type === 'delay' && (
                <DelayStepEditor
                    step={localStep}
                    onChange={handleChange}
                />
            )}

            {/* Tag Step Editor */}
            {localStep.type === 'tag' && (
                <TagStepEditor
                    step={localStep}
                    onChange={handleChange}
                />
            )}

            {/* Footer */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                marginTop: 24,
                paddingTop: 16,
                borderTop: '1px solid #e8e8e8'
            }}>
                <Button onClick={onCancel}>Cancel</Button>
                <Button type="primary" onClick={handleSave}>
                    Save Changes
                </Button>
            </div>
        </div>
    );
}

// Message Step Editor
function MessageStepEditor({ step, onChange, messageApi }) {
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (file) => {
        setUploading(true);
        try {
            const extension = file.name.split('.').pop();
            const path = generateId();

            // Get presigned URL
            const presignedRes = await axios.get(
                `https://4iav7nkzgsu2jbpidrdt7seiri0lcfhk.lambda-url.ap-south-1.on.aws/generate_image_url?post_id=${path}&ext=${extension}&custom_name=file`,
                { headers: { Authorization: localStorage.getItem('token') } }
            );

            // Upload to S3
            await axios.put(presignedRes.data.url, file, {
                headers: { 'Content-Type': file.type }
            });

            const imageUrl = `https://public-assets.instantdm.com/instantdm/public/${path}/media/file.${extension}`;
            onChange('image_url', imageUrl);
            messageApi.success('Image uploaded!');
        } catch (error) {
            messageApi.error('Failed to upload image');
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    const addTemplate = () => {
        const templates = [...(step.templates || [])];
        if (templates.length < 10) {
            templates.push({
                id: generateId(),
                title: '',
                description: '',
                thumb_url: '',
                buttons: []
            });
            onChange('templates', templates);
        }
    };

    const updateTemplate = (index, field, value) => {
        const templates = [...(step.templates || [])];
        templates[index] = { ...templates[index], [field]: value };
        onChange('templates', templates);
    };

    const removeTemplate = (index) => {
        const templates = step.templates.filter((_, i) => i !== index);
        onChange('templates', templates);
    };

    const addButton = (templateIndex) => {
        const templates = [...(step.templates || [])];
        if (templates[templateIndex].buttons.length < 3) {
            templates[templateIndex].buttons.push({
                title: '',
                type: 'postback',
                url: ''
            });
            onChange('templates', templates);
        }
    };

    const updateButton = (templateIndex, buttonIndex, field, value) => {
        const templates = [...(step.templates || [])];
        templates[templateIndex].buttons[buttonIndex] = {
            ...templates[templateIndex].buttons[buttonIndex],
            [field]: value
        };
        onChange('templates', templates);
    };

    const removeButton = (templateIndex, buttonIndex) => {
        const templates = [...(step.templates || [])];
        templates[templateIndex].buttons = templates[templateIndex].buttons.filter(
            (_, i) => i !== buttonIndex
        );
        onChange('templates', templates);
    };

    return (
        <div>
            {/* Text Message */}
            {step.messageType === 'text' && (
                <div className="action-builder-section">
                    <label>Message</label>
                    <TextArea
                        value={step.text}
                        onChange={(e) => onChange('text', e.target.value)}
                        placeholder="Enter your message..."
                        rows={4}
                    />
                </div>
            )}

            {/* Image Message */}
            {step.messageType === 'image' && (
                <div className="action-builder-section">
                    <label>Image</label>
                    {step.image_url ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                                src={step.image_url}
                                alt="Preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 200,
                                    borderRadius: 8
                                }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<CloseOutlined />}
                                style={{ position: 'absolute', top: 8, right: 8 }}
                                onClick={() => onChange('image_url', '')}
                            />
                        </div>
                    ) : (
                        <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={(file) => {
                                handleImageUpload(file);
                                return false;
                            }}
                        >
                            <Button
                                icon={<UploadOutlined />}
                                loading={uploading}
                                block
                            >
                                Upload Image
                            </Button>
                        </Upload>
                    )}
                </div>
            )}

            {/* Button Template */}
            {step.messageType === 'button' && (
                <div className="action-builder-section">
                    <label>
                        Button Templates
                        {step.isProductCard && ' (Product Cards)'}
                    </label>

                    <div className="product-card-carousel">
                        {step.templates?.map((template, tIndex) => (
                            <div key={template.id || tIndex} className="product-card-item">
                                {/* Template Image */}
                                <div
                                    className="product-card-image"
                                    onClick={() => {/* Open image selector */ }}
                                >
                                    {template.thumb_url ? (
                                        <img src={template.thumb_url} alt={template.title} />
                                    ) : (
                                        <div className="upload-placeholder">
                                            <UploadOutlined style={{ fontSize: 24 }} />
                                            <span>Add Image</span>
                                        </div>
                                    )}
                                </div>

                                {/* Template Content */}
                                <div className="product-card-content">
                                    <Input
                                        placeholder="Card Title"
                                        value={template.title}
                                        onChange={(e) => updateTemplate(tIndex, 'title', e.target.value)}
                                        style={{ marginBottom: 8 }}
                                    />
                                    <TextArea
                                        placeholder="Description"
                                        value={template.description}
                                        onChange={(e) => updateTemplate(tIndex, 'description', e.target.value)}
                                        rows={2}
                                    />

                                    {/* Buttons */}
                                    <div className="product-card-buttons">
                                        <Collapse accordion size="small">
                                            {template.buttons?.map((btn, bIndex) => (
                                                <Panel
                                                    key={bIndex}
                                                    header={btn.title || 'New Button'}
                                                    extra={
                                                        <DeleteOutlined
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeButton(tIndex, bIndex);
                                                            }}
                                                        />
                                                    }
                                                >
                                                    <Input
                                                        placeholder="Button Text"
                                                        value={btn.title}
                                                        onChange={(e) => updateButton(tIndex, bIndex, 'title', e.target.value)}
                                                        style={{ marginBottom: 8 }}
                                                    />
                                                    <Select
                                                        value={btn.type}
                                                        onChange={(v) => updateButton(tIndex, bIndex, 'type', v)}
                                                        style={{ width: '100%', marginBottom: 8 }}
                                                        options={[
                                                            { value: 'postback', label: 'Send Message' },
                                                            { value: 'web_url', label: 'Open Link' }
                                                        ]}
                                                    />
                                                    {btn.type === 'web_url' && (
                                                        <Input
                                                            placeholder="https://..."
                                                            value={btn.url}
                                                            onChange={(e) => updateButton(tIndex, bIndex, 'url', e.target.value)}
                                                        />
                                                    )}
                                                </Panel>
                                            ))}
                                        </Collapse>

                                        {template.buttons?.length < 3 && (
                                            <Button
                                                type="dashed"
                                                size="small"
                                                icon={<PlusOutlined />}
                                                onClick={() => addButton(tIndex)}
                                                style={{ marginTop: 8 }}
                                                block
                                            >
                                                Add Button
                                            </Button>
                                        )}
                                    </div>

                                    <Button
                                        type="text"
                                        danger
                                        size="small"
                                        onClick={() => removeTemplate(tIndex)}
                                        style={{ marginTop: 8 }}
                                    >
                                        Remove Card
                                    </Button>
                                </div>
                            </div>
                        ))}

                        {/* Add Template Button */}
                        {(step.templates?.length || 0) < 10 && (
                            <Button
                                type="dashed"
                                onClick={addTemplate}
                                style={{
                                    height: 280,
                                    minWidth: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <PlusOutlined style={{ fontSize: 24 }} />
                                <span style={{ marginTop: 8 }}>Add Card</span>
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Question Step Editor
function QuestionStepEditor({ step, onChange }) {
    return (
        <div>
            <div className="action-builder-section">
                <label>Question Type</label>
                <Select
                    value={step.questionType}
                    onChange={(v) => onChange('questionType', v)}
                    style={{ width: '100%' }}
                    options={[
                        { value: 'name', label: 'Name' },
                        { value: 'email', label: 'Email' },
                        { value: 'phone', label: 'Phone' },
                        { value: 'address', label: 'Address' },
                        { value: 'text', label: 'Free Text' },
                        { value: 'custom_input', label: 'Custom Input' },
                        { value: 'number', label: 'Number' },
                        { value: 'date', label: 'Date' }
                    ]}
                />
            </div>

            <div className="action-builder-section">
                <label>Question Text</label>
                <TextArea
                    value={step.question}
                    onChange={(e) => onChange('question', e.target.value)}
                    placeholder="Enter your question..."
                    rows={2}
                />
            </div>

            {step.questionType !== 'custom_input' && step.questionType !== 'text' && (
                <>
                    <div className="action-builder-section">
                        <label>Invalid Response Message</label>
                        <Input
                            value={step.wrong_input?.message}
                            onChange={(e) => onChange('wrong_input', { message: e.target.value })}
                            placeholder="Message shown when user gives invalid input"
                        />
                    </div>

                    <div className="action-builder-section">
                        <label>Retry Attempts</label>
                        <Select
                            value={step.retry_attempt?.count || 3}
                            onChange={(v) => onChange('retry_attempt', { count: v })}
                            style={{ width: '100%' }}
                            options={[
                                { value: 0, label: 'No retry' },
                                { value: 1, label: '1 attempt' },
                                { value: 2, label: '2 attempts' },
                                { value: 3, label: '3 attempts' },
                                { value: -1, label: 'Until valid' }
                            ]}
                        />
                    </div>
                </>
            )}

            <div className="action-builder-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Allow Skip</span>
                <Switch
                    checked={step.skip_option?.enable}
                    onChange={(v) => onChange('skip_option', { ...step.skip_option, enable: v })}
                />
            </div>

            {step.skip_option?.enable && (
                <div className="action-builder-section">
                    <label>Skip Button Text</label>
                    <Input
                        value={step.skip_option?.skip_text || 'Skip'}
                        onChange={(e) => onChange('skip_option', { ...step.skip_option, skip_text: e.target.value })}
                    />
                </div>
            )}

            <div className="action-builder-section">
                <label>Variable Name (Optional)</label>
                <Input
                    value={step.variable}
                    onChange={(e) => onChange('variable', e.target.value)}
                    placeholder="e.g., user_email"
                />
            </div>
        </div>
    );
}

// Delay Step Editor
function DelayStepEditor({ step, onChange }) {
    return (
        <div>
            <div className="action-builder-section">
                <label>Delay Duration</label>
                <div style={{ display: 'flex', gap: 12 }}>
                    <InputNumber
                        value={step.duration}
                        onChange={(v) => onChange('duration', v)}
                        min={1}
                        max={step.durationType === 'm' ? 60 : 300}
                        style={{ flex: 1 }}
                    />
                    <Select
                        value={step.durationType}
                        onChange={(v) => onChange('durationType', v)}
                        style={{ width: 120 }}
                        options={[
                            { value: 's', label: 'Seconds' },
                            { value: 'm', label: 'Minutes' }
                        ]}
                    />
                </div>
                <Slider
                    value={step.duration}
                    onChange={(v) => onChange('duration', v)}
                    min={1}
                    max={step.durationType === 'm' ? 60 : 300}
                    style={{ marginTop: 12 }}
                />
            </div>

            <div className="action-builder-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Show typing indicator</span>
                <Switch
                    checked={step.typingIndicator}
                    onChange={(v) => onChange('typingIndicator', v)}
                />
            </div>
        </div>
    );
}

// Tag Step Editor
function TagStepEditor({ step, onChange }) {
    const [allTags, setAllTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newTagName, setNewTagName] = useState('');

    // Import Firebase modules dynamically
    useEffect(() => {
        fetchTags();
    }, []);

    const fetchTags = async () => {
        setLoading(true);
        try {
            const { fireDB } = await import('../../../funcs/firebase');
            const { collection, getDocs, query, orderBy } = await import('firebase/firestore');

            const user_detail = JSON.parse(localStorage.getItem("userData"));
            if (!user_detail?.user_id) return;

            const tagsRef = collection(fireDB, "users", user_detail.user_id, "tags");
            const q = query(tagsRef, orderBy("created_at", "desc"));
            const querySnapshot = await getDocs(q);

            const fetchedTags = querySnapshot.docs.map(doc => ({
                label: doc.data().name,
                value: doc.id,
                name: doc.data().name
            }));
            setAllTags(fetchedTags);
        } catch (error) {
            console.error("Error fetching tags:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleTagChange = (values, options) => {
        const tagsData = options.map(opt => ({
            id: opt.value,
            name: opt.label || opt.name
        }));
        onChange('tags', values);
        onChange('tagsData', tagsData);
    };

    const createNewTag = async () => {
        if (!newTagName.trim()) return;

        setLoading(true);
        try {
            const { fireDB } = await import('../../../funcs/firebase');
            const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');

            const user_detail = JSON.parse(localStorage.getItem("userData"));
            if (!user_detail?.user_id) return;

            const tagsRef = collection(fireDB, "users", user_detail.user_id, "tags");
            const docRef = await addDoc(tagsRef, {
                name: newTagName,
                created_at: serverTimestamp()
            });

            const newTag = { label: newTagName, value: docRef.id, name: newTagName };
            setAllTags(prev => [newTag, ...prev]);

            // Auto-select the new tag
            const newTags = [...(step.tags || []), docRef.id];
            const newTagsData = [...(step.tagsData || []), { id: docRef.id, name: newTagName }];
            onChange('tags', newTags);
            onChange('tagsData', newTagsData);

            setNewTagName('');
        } catch (error) {
            console.error("Error creating tag:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="action-builder-section">
                <label>Select Tags</label>
                <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select or create tags"
                    value={step.tags || []}
                    onChange={handleTagChange}
                    options={allTags}
                    loading={loading}
                    maxTagCount="responsive"
                />
            </div>

            <div className="action-builder-section">
                <label>Create New Tag</label>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Input
                        placeholder="New tag name..."
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onPressEnter={createNewTag}
                    />
                    <Button
                        type="primary"
                        onClick={createNewTag}
                        loading={loading}
                        disabled={!newTagName.trim()}
                    >
                        Add
                    </Button>
                </div>
            </div>

            <p style={{ color: '#888', fontSize: 12, marginTop: 16 }}>
                🏷️ Tags will be applied to the contact when the flow reaches this step.
            </p>
        </div>
    );
}
